<script setup>
import {defineModel, defineEmits, defineProps, ref, computed, onBeforeMount} from 'vue'
import {useStore} from "vuex";
import FolderSvg from "@/components/UI/svg/FolderSvg.vue";
import ArrowSortSvg from "@/components/UI/svg/ArrowSortSvg.vue";


const desc = defineModel('desc')
const tables = defineModel('tables')
const folders = defineModel('folders')
const orderBy = defineModel('orderBy')
const folderId = defineModel('folderId')
const isLoadingMore = defineModel('isLoadingMore')

const store = useStore()

const emit = defineEmits(['submit', 'loadMore', 'submitFilesSaving'])

const selectedFolders = ref([])

const selectedTable = computed(
    () => {
        return store.getters['createFile/getFile'](props.connectionType)
    }
)

const isCheckedFiles = computed(() => tables.value.some(table => table.isChecked));


const props = defineProps({
    isLoading: {
        required: true,
        type: Boolean,
    },
    connectionType: {
        required: true,
        type: String,
    },
    search: {
        required: true,
        type: String,
    },
    nextPageToken: {
        type: String,
    },
    multiSelect: {
        type: Boolean,
        required: true,
    }
})


onBeforeMount(() => {
    for (let i = 0; i < tables.value.length; i++) {
        if (tables.value[i]?.id === selectedTable.value[i]?.id) {
            tables.value[i].isChecked = true
        }
    }
})


function saveFiles() {
    let bufFiles = []
    for (let i = 0; i < tables.value.length; i++) {
        if (tables.value[i].isChecked) {
            bufFiles.push(tables.value[i])
        }
    }

    emit('submit')
    emit('submitFilesSaving', bufFiles)
}

function clearSelectedFile() {
    tables.value.forEach(table => {
        table.isChecked = false;
    });
    isCheckedFiles.value = false;
}

function sortByName() {
    if (orderBy.value === 'name_natural') {
        desc.value = !desc.value
    }
    else {
        orderBy.value = 'name_natural'
        desc.value = false
    }
}


function sortByLastUpdatedTime() {
    if (orderBy.value === 'modifiedTime') {
        desc.value = !desc.value
    }
    else {
        orderBy.value = 'modifiedTime'
        desc.value = false
    }
}

function selectFolder(folder) {
    selectedFolders.value.push(folder)
    folderId.value = folder.id
}

function clearFolder() {
    if (folderId.value === "") {
        return
    }
    folderId.value = ""
    selectedFolders.value.splice(0)
}

function goToFolder(folder) {

    if (folder.id === folderId.value) {
        return
    }
    let index = 0
    for (let i = 0; i < selectedFolders.value.length; i++) {
        if (selectedFolders.value[i]?.id === folder.id) {
            index = i
        }
    }

    selectedFolders.value.splice(index + 1)

    folderId.value = folder.id
}

function handleCheckboxChange(checkedTable) {
    if (!props.multiSelect) {
        // Если разрешен только одиночный выбор, снимаем выделение со всех других таблиц
        tables.value.forEach(table => {
            table.isChecked = table.id === checkedTable.id;
        });
    } else {
        const table  = tables.value.find(
            value => value.id === checkedTable.id
        )
        // Иначе оставляем как есть (множественный выбор)
        table.isChecked = !table.isChecked;
    }
}

</script>

<template>
    <div class="w-full flex flex-col">
        <div class="w-full h-14 flex items-center overflow-hidden" v-if="selectedFolders.length !== 0">
            <div class="size-10 ml-2 flex justify-center items-center rounded-md cursor-pointer transition-all duration-200 hover:bg-gray-100" v-on:click="clearFolder">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1399.79 1250.67" class="size-6"><path d="M125.83,1071.89l61.73,106.63c12.82,22.44,31.26,40.08,52.91,52.91L460.94,849.81H20A145.24,145.24,0,0,0,39.24,922Z" transform="translate(-20)" style="fill: rgb(0, 102, 218);"></path><path d="M719.89,400.86,499.42,19.24c-21.64,12.83-40.08,30.47-52.91,52.91L39.24,777.66A145.31,145.31,0,0,0,20,849.81H460.94Z" transform="translate(-20)" style="fill: rgb(0, 172, 71);"></path><path d="M1199.32,1231.43c21.64-12.83,40.08-30.47,52.91-52.91l25.65-44.1L1400.55,922a145.34,145.34,0,0,0,19.24-72.16h-441l93.84,184.4Z" transform="translate(-20)" style="fill: rgb(234, 67, 53);"></path><path d="M719.89,400.86,940.36,19.24C918.72,6.41,893.87,0,868.21,0H571.58c-25.66,0-50.51,7.22-72.16,19.24Z" transform="translate(-20)" style="fill: rgb(0, 131, 45);"></path><path d="M978.85,849.81H460.94L240.47,1231.43c21.65,12.83,46.5,19.24,72.15,19.24h814.54c25.66,0,50.51-7.22,72.16-19.24Z" transform="translate(-20)" style="fill: rgb(38, 132, 252);"></path><path d="M1196.91,424.91,993.28,72.15C980.45,49.71,962,32.07,940.36,19.24L719.89,400.86l259,448.95H1419a145.24,145.24,0,0,0-19.25-72.15Z" transform="translate(-20)" style="fill: rgb(255, 186, 0);"></path></svg>
            </div>
            <div v-if="selectedFolders.length > 3">
                <svg class="size-6 fill-gray-600" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path></svg>
            </div>
            <div class="size-10 ml-2 px-2 flex items-center justify-center" v-if="selectedFolders.length > 3">
                <svg class="fill-gray-600" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 10c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm12 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm-6 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"></path></svg>
            </div>
            <div class="h-14 flex items-center" v-for="folder in selectedFolders.slice(-3)" v-bind:key="folder">
                <div>
                    <svg class="size-6 fill-gray-600" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path></svg>
                </div>
                <div class="text-sm font-bold cursor-pointer mx-2 px-2 py-1 block max-w-96 overflow-hidden text-ellipsis whitespace-nowrap rounded-md select-none transition-all duration-200 hover:bg-gray-100" v-on:click="goToFolder(folder)">
                    {{ folder.name }}
                </div>
            </div>
        </div>
        <div class="w-full h-full" v-if="!props.isLoading">
            <div v-if="folders.length === 0 && tables.length === 0" class="w-full h-full flex items-center justify-center">
                <div class="flex flex-col items-center justify-center">
                    <div class="flex flex-col items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 500" class="size-40">
                            <circle cx="250" cy="250" r="227" style="fill: rgb(245, 245, 245);"></circle>
                            <path d="M 292.2 272.2 h -8 l -2.8 -2.6 C 291.2 258.4 297.2 244.1 297.2 228.4 C 297.2 193.4 267.8 165 231.6 165 S 166 193.4 166 228.4 S 195.3 291.7 231.6 291.7 c 16.2 0 31.2 -5.7 42.6 -15.3 l 2.8 2.7 v 7.7 l 50.5 48.7 L 342.5 321 l -50.3 -48.8 z m -60.6 0 C 206.4 272.2 186.2 252.6 186.2 228.4 S 206.4 184.4 231.6 184.4 S 276.9 204.1 276.9 228.4 S 256.7 272.2 231.6 272.2 z" style="fill: rgb(220, 220, 220);"></path>
                        </svg>
                        <div class="flex flex-col items-center justify-center mx-4">
                            <div class="text-base font-bold">
                                Совпадений не найдено
                            </div>
                            <div>
                                Мы не нашли файлов соответсвующих запросу на вашем <span class="font-bold"> Google диске </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <div v-else class="w-full h-full overflow-y-auto flex flex-col px-4 relative">
                <div class="flex h-14 sticky z-10 top-0 bg-white">
                    <div class="h-14 w-[calc(100%-356px)] flex justify-start pl-5 items-center font-bold text-sm text-gray-500 hover:text-gray-800 cursor-pointer"
                         v-on:click="sortByName"
                    >
                        <div class="select-none">
                            Папки
                        </div>
                        <div class="flex items-center justify-center select-none" :class="{'rotate-180': !desc}">
                            <ArrowSortSvg class="w-4 h-4 fill-gray-500" />
                        </div>
                    </div>
                </div>
                <div class="flex flex-col w-full">
                    <div class="h-14 hover:bg-gray-100 cursor-pointer flex items-center" v-for="folder in folders" v-bind:key="folder" v-on:click="selectFolder(folder)">
                        <div class="flex items-center justify-center size-14">
                            <FolderSvg class="size-10"/>
                        </div>
                        <div class="w-[calc(80%)] sm:w-[calc(100%-500px)] overflow-hidden text-ellipsis whitespace-nowrap select-none">{{ folder.name }}</div>
                    </div>
                </div>
                <div class="flex h-14 sticky z-10 top-0 bg-white">
                    <div class="h-14 w-[calc(100%-356px)] flex justify-start pl-5 items-center font-bold text-sm text-gray-500 hover:text-gray-800 cursor-pointer"
                         v-on:click="sortByName"
                    >
                        <div class="select-none">
                            Файлы
                        </div>
                        <div
                            class="flex items-center justify-center select-none" :class="{'rotate-180': !desc}"
                            v-if="orderBy === 'name_natural'"
                        >
                            <ArrowSortSvg class="w-4 h-4 fill-gray-500" />
                        </div>
                    </div>
                    <div class="w-20 justify-center items-center font-bold text-xs text-gray-500 select-none hidden lg:flex">
                        Владелец
                    </div>
                    <div class="w-20 justify-center items-center font-bold text-xs text-gray-500 select-none hidden sm:flex">
                        Тип
                    </div>
                    <div class="mx-auto justify-center items-center font-bold text-xs text-gray-500 cursor-pointer hover:text-gray-800 hidden sm:flex"
                         v-on:click="sortByLastUpdatedTime"
                    >
                        <div class="select-none">
                            Последнее изменение
                        </div>
                        <div
                            class="flex items-center justify-center select-none" :class="{'rotate-180': !desc}"
                            v-if="orderBy === 'modifiedTime'"
                        >
                            <ArrowSortSvg class="w-4 h-4 fill-gray-500" />
                        </div>
                    </div>
                </div>
                <div class="w-full h-full relative">
                    <div class="flex items-center h-14 border border-transparent rounded-md"
                         v-for="table in tables" v-bind:key="table"
                         :class="{
                         'bg-green-100': table.isChecked,
                         'bg-gray-100': table.isChecked,
                         'hover:bg-gray-100': !table.isChecked,
                         'border-green-400': !table.isChecked
                     }"
                    >
                        <div class="h-14 w-14 flex justify-center items-center">
                            <div class="">
                                <input
                                    type="checkbox"
                                    class="checkbox cursor-pointer"
                                    v-model="table.isChecked"
                                    v-on:click="handleCheckboxChange(table)"
                                >
                            </div>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 852.4 852.4" class="w-9 h-9 mr-5"><rect width="852.4" height="852.4" rx="72.71" style="fill: rgb(35, 165, 102);"></rect><path d="M654.74,624.11H197.66V228.29H654.74V624.11ZM454.47,284.83v70.69H598.19V284.83Zm0,113.1v66H598.19v-66Zm0,108.38v61.26H598.19V506.31Zm-56.55,61.26V506.31H254.21v61.26Zm0-103.67v-66H254.21v66Zm0-108.38V284.83H254.21v70.69Z" style="fill: rgb(255, 255, 255); fill-rule: evenodd;"></path></svg>
                        <div class="w-[calc(80%)] sm:w-[calc(100%-500px)] overflow-hidden text-ellipsis whitespace-nowrap select-none">{{ table.name }}</div>
                        <div class="w-16 overflow-hidden text-ellipsis whitespace-nowrap font-bold text-xs text-gray-500 ml-10 select-none hidden lg:block">
                            {{table.owners[0].displayName}}
                        </div>
                        <div class="w-16 overflow-hidden text-ellipsis whitespace-nowrap font-bold text-xs text-gray-500 ml-10 select-none hidden sm:block">
                            Google Sheet
                        </div>
                        <div class="mx-auto justify-center items-center font-bold text-xs text-gray-500 select-none hidden sm:flex">
                            {{ table.modifiedTime }}
                        </div>
                    </div>
                    <div class="h-14 w-full flex" v-if="nextPageToken !== '' && nextPageToken !== undefined">
                        <div class="w-full h-full flex items-center justify-center">
                            <div v-if="!isLoadingMore" class="h-10 w-40 bg-blue-600 flex items-center justify-center rounded-md cursor-pointer hover:bg-blue-700"
                                 v-on:click="emit('loadMore')"
                            >
                                <div class="mr-2">
                                    <svg class="size-6 fill-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path data-v-036d9b29="" d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4V7zm-1-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path></svg>
                                </div>
                                <div class="text-white font-bold text-sm">
                                    Загрузить ещё
                                </div>
                            </div>
                            <div v-else class="h-10 w-40 bg-blue-600 flex items-center justify-center rounded-md cursor-not-allowed hover:bg-blue-700">
                                <svg class="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="white" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div class="sticky bottom-0 w-full py-4 bg-gray-100 hidden justify-between items-center lg:flex" v-if="isCheckedFiles">
                        <div
                            class="ml-5 flex hover:bg-gray-300 py-2 px-3 cursor-pointer rounded-md select-none transition-all duration-150"
                            v-on:click="clearSelectedFile"
                        >
                            <div class="font-bold select-none">
                                x
                            </div>
                            <div class="ml-2 font-bold select-none">
                                Очистить
                            </div>
                        </div>
                        <div
                            class="mr-8 py-2 px-3 bg-blue-600 select-none font-bold cursor-pointer text-white rounded-md hover:bg-blue-800 transition-all duration-150"
                            v-on:click="saveFiles"
                        >
                            Готово
                        </div>
                    </div>
                </div>
                <div class="fixed bottom-0 w-full py-4 bg-gray-100 flex justify-between items-center lg:hidden" v-if="isCheckedFiles">
                    <div
                        class="ml-5 flex hover:bg-gray-300 py-2 px-3 cursor-pointer rounded-md select-none transition-all duration-150"
                        v-on:click="clearSelectedFile"
                    >
                        <div class="font-bold select-none">
                            x
                        </div>
                        <div class="ml-2 font-bold select-none">
                            Очистить
                        </div>
                    </div>
                    <div
                        class="mr-8 py-2 px-3 bg-blue-600 select-none font-bold cursor-pointer text-white rounded-md hover:bg-blue-800 transition-all duration-150"
                        v-on:click="saveFiles"
                    >
                        Готово
                    </div>
                </div>
            </div>

        </div>
        <div v-else class="w-full h-full flex flex-col mt-5">
            <div class="flex items-center h-14 border border-transparent animate-pulse"
                 v-for="index in 8" v-bind:key="index"
            >
                <div class="ml-7 h-10 w-10 bg-slate-200 mr-10"></div>
                <div class="w-[calc(100%-500px)] h-2 overflow-hidden text-ellipsis whitespace-nowrap select-none bg-slate-200"></div>
                <div class="w-16 h-2 bg-slate-200 ml-12 select-none"></div>
                <div class="w-16 h-2 bg-slate-200 ml-12 select-none"></div>
                <div class="w-16 h-2 bg-slate-200 ml-10 select-none"></div>
            </div>
        </div>

    </div>
</template>

<style scoped>
.checkbox {
    appearance: none;
    background-color: #ffffff;
    margin: 0;
    width: 18px;
    height: 18px;
    border-radius: 3px;
    display: grid;
    place-content: center;
    user-select: none;
    border: 2px solid black;
}
.checkbox::before {
    content: "";
    width: 8px;
    height: 8px;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em #0A84FF;
}
.checkbox:checked::before {
    transform: scale(1);
}
.checkbox:checked {
    border-color: #0A84FF;
}
</style>